import React from 'react';
import Lightbox from '@enroly/react-images';
import DocPreviewToolbar from './toolbar';

interface IProps {
  documents: string[];
  currentDocument?: number;
  onClose: () => void;
}

interface IState {
  currentImage: number;
  images: Image[];
}

interface Image {
  src: string;
  initialRotation: number;
  initialZoom: number;
}

const SCALE_STEP = 0.1;
const INITIAL_ZOOM = 1.1;

export class DocumentPreview extends React.Component<IProps, IState> {
  interval: NodeJS.Timeout | undefined;
  elements: NodeList | undefined;
  constructor(props: IProps) {
    super(props);
    this.state = {
      currentImage: 0,
      images: props.documents.length
        ? props.documents.map((item: string) => {
            return {
              src: item,
              initialRotation: 0,
              initialZoom: INITIAL_ZOOM
            };
          })
        : []
    };
    this.handleImagePosition = this.handleImagePosition.bind(this);
  }

  componentDidMount() {
    this.interval = setInterval(() => {
      this.elements = document.querySelectorAll("button[class^='actionButton']");
      if (this.elements && this.elements.length) {
        if(this.interval) clearInterval(this.interval);

        this.elements[0].addEventListener('click', () => this.handleImagePosition(false)); // Zoom In
        this.elements[1].addEventListener('click', () => this.handleImagePosition(false)); // Zoom Out
      }
    }, 100);
  }

  componentDidUpdate(_prevProps: IProps, prevState: IState) {
    if (JSON.stringify(prevState) !== JSON.stringify(this.state)) {
      this.setState(this.state);
      setTimeout(() => {
        this.handleImagePosition(true);
      }, 0);
    }
  }

  componentWillUnmount() {
    if (this.interval) clearInterval(this.interval);

    if (this.elements && this.elements.length) {
      this.elements[0].removeEventListener('click', () => this.handleImagePosition(false)); // Zoom In
      this.elements[1].removeEventListener('click', () => this.handleImagePosition(false)); // Zoom Out
    }
  }

  handleImagePosition = (initialState?: boolean) => {
    const docPreview = document.querySelector("figure[class^='figure']");
    const image = document.querySelector("img[class^='image']");

    if (image && docPreview) {
      const previewParams = docPreview?.getBoundingClientRect();

      const params = image.getBoundingClientRect();
      let imageStyle = image.getAttribute('style');

      if (params && imageStyle) {
        const scaleStr = imageStyle.match(/scaleX\(([^)]*)\)/);
        if (scaleStr) {
          const scaleValue = Number(scaleStr[1]);

          const initialHeight = params.height / scaleValue;
          const nextHeight = initialState ? initialHeight * scaleValue : initialHeight * (scaleValue + SCALE_STEP);
          const topValue = (nextHeight - initialHeight) / 2;

          const initialWidth = params.width / scaleValue;
          const nextWidth = initialState ? initialWidth * scaleValue : initialWidth * (scaleValue + SCALE_STEP);
          const leftValue = (nextWidth - initialWidth) / 2;

          // remove top/left
          imageStyle = imageStyle.replace(/top:([^)]*)/g, '');

          // set calculated top/left
          image.setAttribute(
            'style',
            `${imageStyle} ` +
              (nextHeight >= previewParams.height ? `top: ${topValue}px;` : '') +
              (nextWidth >= previewParams.width ? `left: ${leftValue}px;` : '')
          );
        }
      }
    }
  };

  goToPrevious = () => {
    this.setState({ currentImage: this.state.currentImage - 1 });
  };

  goToNext = () => {
    this.setState({ currentImage: this.state.currentImage + 1 });
  };

  goToImage = (index: number) => {
    this.setState({ currentImage: index });
  };

  handleClickImage = () => {
    if (this.state.currentImage === this.state.images.length - 1) return;
    this.goToNext();
  };

  handleSaveImage = (currentImageIndex: number, params: any) => {
    this.setState({
      images: [
        ...this.state.images.slice(0, currentImageIndex),
        {
          ...this.state.images[currentImageIndex],
          initialRotation: params.rotation,
          initialZoom: params.zoom
        },
        ...this.state.images.slice(currentImageIndex + 1)
      ]
    });
  };

  render() {
    return this.state.images.length ? (
      <Lightbox
        currentImage={this.state.currentImage}
        customControls={[<DocPreviewToolbar />]}
        images={this.state.images}
        isOpen={true}
        showCloseButton={true}
        rotatable={true}
        zoomable={true}
        showThumbnails={true}
        theme={{
          content: { marginBottom: 12 },
          footer: {
            color: '#d7d7d7',
            paddingRight: 120,
            paddingLeft: 120,
            top: 0
          },
          toolbarAction: { fill: 'transparent' },
          close: { fill: '#d7d7d7', top: 16, marginRight: 20 },
          arrow: { fill: '#d7d7d7', zIndex: 1001 },
          figure: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'auto',
            backgroundColor: 'rgba(0, 0, 0, 0.25)',
            zIndex: 1000,
            width: '92%',
            height: '83%'
          },
          image: {
            position: 'absolute'
          }
        }}
        width={5000}
        onClickPrev={() => this.goToPrevious()}
        onClickNext={() => this.goToNext()}
        onClickImage={() => this.handleClickImage()}
        onClickThumbnail={(index: number) => this.goToImage(index)}
        onClose={() => this.props.onClose()}
        onSave={(currentImageIndex: number, params: any) => this.handleSaveImage(currentImageIndex, params)}
      />
    ) : null;
  }
}
